import { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { products } from '@/mocks/products';
import { useCart } from '@/hooks/useCart';

type TabKey = 'description' | 'specs' | 'installation';

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addItem, setIsOpen } = useCart();
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);

  const [activeTab, setActiveTab] = useState<TabKey>('description');
  const sectionRef = useRef<HTMLDivElement>(null);

  const product = products.find((p) => p.id === Number(id));

  const related = product
    ? products
        .filter((p) => p.id !== product.id && (p.category === product.category || p.brand === product.brand))
        .slice(0, 4)
    : [];

  useEffect(() => {
    if (!product) return;
    setQty(1);
    setAdded(false);
    setActiveTab('description');
    window.scrollTo(0, 0);
  }, [product?.id]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-up');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    const els = sectionRef.current?.querySelectorAll('.reveal');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [product?.id]);

  if (!product) {
    return (
      <div className="font-sans bg-white min-h-screen">
        <Navbar />
        <div className="pt-32 pb-20 text-center">
          <i className="ri-error-warning-line text-4xl text-[#1C1C1C]/20 mb-4"></i>
          <h1 className="font-serif text-2xl font-bold text-[#1C1C1C] mb-2">პროდუქტი ვერ მოიძებნა</h1>
          <p className="text-sm text-[#1C1C1C]/40 mb-6">მოთხოვნილი პროდუქტი არ არსებობს</p>
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 bg-[#ec3145] text-white font-semibold px-6 py-3 hover:bg-[#c9283a] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            მაღაზიაში დაბრუნება
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const handleAddToCart = () => {
    addItem({
      id: product.id,
      name: product.name,
      brand: product.brand,
      price: product.price,
      image: product.image,
      category: product.category,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const handleBuyNow = () => {
    addItem({
      id: product.id,
      name: product.name,
      brand: product.brand,
      price: product.price,
      image: product.image,
      category: product.category,
    });
    setIsOpen(true);
  };

  const specs = [
    { label: 'ბრენდი', value: product.brand },
    { label: 'კატეგორია', value: product.category },
    { label: 'მასალა', value: product.material },
    { label: 'ზომა', value: product.size },
    { label: 'სისქე', value: product.thickness || '—' },
    { label: 'წარმოშობა', value: product.origin },
  ];

  const tabs: { key: TabKey; label: string; icon: string }[] = [
    { key: 'description', label: 'აღწერა', icon: 'ri-file-text-line' },
    { key: 'specs', label: 'ტექნიკური მახასიათებლები', icon: 'ri-list-check-2' },
    { key: 'installation', label: 'მონტაჟი', icon: 'ri-tools-line' },
  ];

  // Cast to access optional fields
  const p = product as typeof product & {
    longDescription?: string;
    features?: string[];
    installation?: string;
  };

  return (
    <div className="font-sans bg-white">
      <Navbar />

      {/* Breadcrumb */}
      <div className="pt-24 md:pt-28 pb-4 bg-[#F5F2EE]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-2 text-xs text-[#1C1C1C]/40">
            <Link to="/" className="hover:text-[#ec3145] transition-colors cursor-pointer">მთავარი</Link>
            <i className="ri-arrow-right-s-line text-[10px]"></i>
            <Link to="/shop" className="hover:text-[#ec3145] transition-colors cursor-pointer">მაღაზია</Link>
            <i className="ri-arrow-right-s-line text-[10px]"></i>
            <span className="text-[#1C1C1C]/60">{product.name}</span>
          </div>
        </div>
      </div>

      <div ref={sectionRef}>
        {/* Product Hero */}
        <section className="bg-[#F5F2EE] pb-12 md:pb-20">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
              {/* Image */}
              <div className="reveal opacity-0">
                <div className="relative aspect-[4/5] bg-white overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                  {product.tag && (
                    <div className="absolute top-5 left-5 bg-[#ec3145] text-white text-[10px] font-bold tracking-[0.2em] uppercase px-3.5 py-1.5">
                      {product.tag}
                    </div>
                  )}
                </div>
              </div>

              {/* Info */}
              <div className="reveal opacity-0 flex flex-col justify-center" style={{ animationDelay: '0.1s' }}>
                <span className="text-[10px] font-bold tracking-[0.35em] uppercase text-[#ec3145] mb-3">
                  {product.brand}
                </span>
                <h1 className="font-serif text-3xl md:text-5xl font-bold text-[#1C1C1C] leading-tight mb-4">
                  {product.name}
                </h1>
                <p className="text-sm text-[#1C1C1C]/50 leading-relaxed mb-6 max-w-md">
                  {product.description}
                </p>

                {/* Price */}
                <div className="flex items-baseline gap-3 mb-8 pb-8 border-b border-[#1C1C1C]/8">
                  <span className="font-serif text-4xl md:text-5xl font-bold text-[#1C1C1C]">
                    ₾{product.price}
                  </span>
                  <span className="text-xs text-[#1C1C1C]/30">
                    {product.origin} / ერთეული
                  </span>
                </div>

                {/* Quantity + Actions */}
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <div className="flex items-center border border-[#1C1C1C]/15 bg-white">
                    <button
                      onClick={() => setQty(Math.max(1, qty - 1))}
                      className="w-12 h-12 flex items-center justify-center text-[#1C1C1C]/40 hover:text-[#1C1C1C] transition-colors cursor-pointer"
                      aria-label="შემცირება"
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    <span className="w-12 h-12 flex items-center justify-center text-sm font-bold text-[#1C1C1C]">
                      {qty}
                    </span>
                    <button
                      onClick={() => setQty(qty + 1)}
                      className="w-12 h-12 flex items-center justify-center text-[#1C1C1C]/40 hover:text-[#1C1C1C] transition-colors cursor-pointer"
                      aria-label="გაზრდა"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>

                  <button
                    onClick={handleAddToCart}
                    className={`flex-1 flex items-center justify-center gap-2 font-semibold text-sm py-3.5 transition-all duration-300 cursor-pointer whitespace-nowrap ${
                      added
                        ? 'bg-[#1C1C1C] text-white'
                        : 'bg-[#ec3145] text-white hover:bg-[#c9283a]'
                    }`}
                  >
                    <i className={added ? 'ri-check-line' : 'ri-shopping-cart-line'}></i>
                    {added ? 'დამატებულია' : 'კალათაში დამატება'}
                  </button>

                  <button
                    onClick={handleBuyNow}
                    className="flex-1 flex items-center justify-center gap-2 border-2 border-[#1C1C1C] text-[#1C1C1C] font-semibold text-sm py-3.5 hover:bg-[#1C1C1C] hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-bank-card-line"></i>
                    ახლავე ყიდვა
                  </button>
                </div>

                {/* Quick specs row */}
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { icon: 'ri-ruler-line', label: 'ზომა', value: product.size },
                    { icon: 'ri-stack-line', label: 'სისქე', value: product.thickness || '—' },
                    { icon: 'ri-map-pin-line', label: 'წარმოშობა', value: product.origin },
                  ].map((s) => (
                    <div key={s.label} className="text-center p-3 bg-white border border-[#1C1C1C]/5">
                      <i className={`${s.icon} text-sm text-[#ec3145] mb-1.5 block`}></i>
                      <span className="text-[10px] text-[#1C1C1C]/30 uppercase tracking-wider block">{s.label}</span>
                      <span className="text-xs font-semibold text-[#1C1C1C] block mt-0.5">{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Tabbed Detail Section */}
        <section className="py-12 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            {/* Tab Nav */}
            <div className="reveal opacity-0 flex flex-wrap gap-0 border-b border-[#1C1C1C]/8 mb-10">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-6 py-4 text-xs font-bold tracking-[0.15em] uppercase transition-all duration-200 cursor-pointer whitespace-nowrap border-b-2 -mb-px ${
                    activeTab === tab.key
                      ? 'border-[#ec3145] text-[#1C1C1C]'
                      : 'border-transparent text-[#1C1C1C]/35 hover:text-[#1C1C1C]/70'
                  }`}
                >
                  <i className={`${tab.icon} text-sm`}></i>
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="reveal opacity-0">
              {/* Description Tab */}
              {activeTab === 'description' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
                  {/* Long description */}
                  <div className="lg:col-span-2">
                    {p.longDescription ? (
                      <div className="space-y-5">
                        {p.longDescription.split('\n\n').map((para, i) => (
                          <p key={i} className="text-sm text-[#1C1C1C]/65 leading-[1.9]">
                            {para}
                          </p>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-[#1C1C1C]/65 leading-[1.9]">{product.description}</p>
                    )}
                  </div>

                  {/* Feature highlights */}
                  <div>
                    <h3 className="font-serif text-lg font-bold text-[#1C1C1C] mb-6">
                      მახასიათებლები
                    </h3>
                    {p.features && p.features.length > 0 ? (
                      <ul className="flex flex-col gap-3">
                        {p.features.map((feat) => (
                          <li key={feat} className="flex items-start gap-3">
                            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <i className="ri-checkbox-circle-fill text-[#ec3145] text-base"></i>
                            </div>
                            <span className="text-sm text-[#1C1C1C]/70 leading-relaxed">{feat}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-[#1C1C1C]/40">ინფორმაცია არ არის</p>
                    )}

                    {/* Brand badge */}
                    <div className="mt-8 pt-8 border-t border-[#1C1C1C]/6">
                      <span className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#1C1C1C]/30 block mb-2">
                        ოფიციალური დისტრიბუტორი
                      </span>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 flex items-center justify-center bg-[#ec3145]/10">
                          <i className="ri-shield-check-line text-[#ec3145] text-sm"></i>
                        </div>
                        <div>
                          <span className="text-sm font-bold text-[#1C1C1C]">{product.brand}</span>
                          <span className="text-xs text-[#1C1C1C]/35 block">{product.origin}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Specs Tab */}
              {activeTab === 'specs' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
                  <div className="lg:col-span-2">
                    <div className="border border-[#1C1C1C]/8">
                      {specs.map((spec, i) => (
                        <div
                          key={spec.label}
                          className={`flex items-center justify-between px-5 py-4 ${
                            i !== specs.length - 1 ? 'border-b border-[#1C1C1C]/5' : ''
                          } ${i % 2 === 0 ? 'bg-white' : 'bg-[#F5F2EE]/50'}`}
                        >
                          <span className="text-sm text-[#1C1C1C]/40">{spec.label}</span>
                          <span className="text-sm font-semibold text-[#1C1C1C]">{spec.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Delivery & Support */}
                  <div>
                    <h3 className="font-serif text-lg font-bold text-[#1C1C1C] mb-6">
                      მიწოდება & მხარდაჭერა
                    </h3>
                    <div className="flex flex-col gap-4">
                      {[
                        {
                          icon: 'ri-truck-line',
                          title: 'მიწოდება მთელს საქართველოში',
                          desc: 'თბილისში 1-2 სამუშაო დღე, რეგიონებში 3-5 დღე.',
                        },
                        {
                          icon: 'ri-tools-line',
                          title: 'მონტაჟის კონსულტაცია',
                          desc: 'ჩვენი ტექნიკური გუნდი დაგეხმარებათ სწორი მონტაჟის ინსტრუქციებში.',
                        },
                        {
                          icon: 'ri-shield-check-line',
                          title: 'ოფიციალური გარანტია',
                          desc: 'ყველა პროდუქტს აქვს მწარმოებლის ოფიციალური გარანტია.',
                        },
                        {
                          icon: 'ri-customer-service-2-line',
                          title: 'ტექნიკური მხარდაჭერა',
                          desc: 'განსაკუთრებული პირობები მშენებლობის კომპანიებისთვის.',
                        },
                      ].map((item) => (
                        <div key={item.title} className="flex gap-4 p-4 bg-[#F5F2EE] border border-[#1C1C1C]/5">
                          <div className="w-10 h-10 flex items-center justify-center bg-white flex-shrink-0">
                            <i className={`${item.icon} text-[#ec3145]`}></i>
                          </div>
                          <div>
                            <h4 className="font-serif text-sm font-bold text-[#1C1C1C] mb-1">{item.title}</h4>
                            <p className="text-xs text-[#1C1C1C]/40 leading-relaxed">{item.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Installation Tab */}
              {activeTab === 'installation' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
                  <div className="lg:col-span-2">
                    <div className="flex items-start gap-4 mb-8 p-5 bg-[#F5F2EE] border-l-2 border-[#ec3145]">
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                        <i className="ri-information-line text-[#ec3145]"></i>
                      </div>
                      <p className="text-xs text-[#1C1C1C]/55 leading-relaxed">
                        ქვემოთ მოცემული ინსტრუქცია ზოგადი სახელმძღვანელოა. სრული ტექნიკური ინსტრუქციისთვის მიმართეთ ჩვენს სპეციალისტებს.
                      </p>
                    </div>

                    {p.installation ? (
                      <div className="space-y-4">
                        {p.installation.split('. ').filter(Boolean).map((step, i) => (
                          <div key={i} className="flex gap-4">
                            <div className="w-7 h-7 flex items-center justify-center bg-[#1C1C1C] text-white text-[11px] font-bold flex-shrink-0 mt-0.5">
                              {i + 1}
                            </div>
                            <p className="text-sm text-[#1C1C1C]/65 leading-relaxed pt-0.5">
                              {step.trim()}{step.trim().endsWith('.') ? '' : '.'}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-[#1C1C1C]/40">მონტაჟის ინსტრუქცია მალე დაემატება.</p>
                    )}
                  </div>

                  {/* CTA panel */}
                  <div>
                    <div className="bg-[#1C1C1C] p-6">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#ec3145]/20 mb-4">
                        <i className="ri-customer-service-2-line text-[#ec3145]"></i>
                      </div>
                      <h3 className="font-serif text-lg font-bold text-white mb-3">
                        პროფესიული კონსულტაცია
                      </h3>
                      <p className="text-xs text-white/40 leading-relaxed mb-5">
                        ჩვენი ტექნიკური გუნდი დაგეხმარებათ სწორი მონტაჟის დაგეგმვაში, მასალების გამოთვლასა და ინსტრუქციებში.
                      </p>
                      <a
                        href="tel:+995599872020"
                        className="flex items-center gap-2 text-xs font-bold tracking-[0.15em] uppercase text-[#ec3145] hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-phone-line"></i>
                        +995 599 87 20 20
                      </a>
                    </div>

                    <div className="mt-4 p-5 border border-[#1C1C1C]/8">
                      <h4 className="font-serif text-sm font-bold text-[#1C1C1C] mb-3">
                        საჭირო ინსტრუმენტები
                      </h4>
                      <ul className="flex flex-col gap-2">
                        {['ბრილიანტის დისკი', 'კბილანა სპატული', 'ელ. მიქსერი', 'სიბრტყის ლაზერი', 'გომბეშო'].map((tool) => (
                          <li key={tool} className="flex items-center gap-2 text-xs text-[#1C1C1C]/50">
                            <i className="ri-checkbox-blank-circle-fill text-[4px] text-[#ec3145]"></i>
                            {tool}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Related Products */}
        {related.length > 0 && (
          <section className="py-12 md:py-20 bg-[#F5F2EE]">
            <div className="max-w-7xl mx-auto px-6 md:px-10">
              <div className="reveal opacity-0 flex items-end justify-between mb-10 pb-6 border-b border-[#1C1C1C]/8">
                <div>
                  <span className="text-[10px] font-bold tracking-[0.35em] uppercase text-[#ec3145] block mb-2">
                    მსგავსი
                  </span>
                  <h2 className="font-serif text-2xl md:text-4xl font-bold text-[#1C1C1C]">
                    მსგავსი პროდუქტები
                  </h2>
                </div>
                <Link
                  to="/shop"
                  className="hidden md:inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.2em] uppercase text-[#1C1C1C]/50 hover:text-[#ec3145] transition-colors cursor-pointer whitespace-nowrap"
                >
                  ყველა პროდუქტი
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              <div className="reveal opacity-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {related.map((rp) => (
                  <Link
                    key={rp.id}
                    to={`/product/${rp.id}`}
                    className="group bg-white overflow-hidden cursor-pointer transition-all duration-500 hover:shadow-lg"
                  >
                    <div className="relative h-56 overflow-hidden">
                      <img
                        src={rp.image}
                        alt={rp.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      {rp.tag && (
                        <div className="absolute top-3 left-3 bg-[#ec3145] text-white text-[10px] font-bold tracking-[0.2em] uppercase px-2.5 py-1">
                          {rp.tag}
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <span className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#ec3145]">
                        {rp.brand}
                      </span>
                      <h3 className="font-serif text-base font-bold text-[#1C1C1C] mt-1 mb-2 group-hover:text-[#ec3145] transition-colors duration-300">
                        {rp.name}
                      </h3>
                      <div className="flex items-center justify-between">
                        <span className="font-serif text-lg font-bold text-[#1C1C1C]">
                          ₾{rp.price}
                        </span>
                        <span className="text-[10px] text-[#1C1C1C]/30">{rp.origin}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>

      <Footer />


    </div>
  );
}
